import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLSections = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Semantic HTML5</div>
                <h1>Content Sections (section, article, aside)</h1>
                <p className="hero-lead">
                    Master the semantic elements for organizing content. Learn when to use section vs article,
                    how to structure complex layouts, and create accessible content hierarchies.
                </p>
            </div>

            <div className="content-section">
                <h2>The &lt;section&gt; Element</h2>
                <p>
                    A thematic grouping of content, typically with a heading. Use when content forms a distinct section.
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<article>
    <h1>Complete Guide to HTML</h1>
    
    <section>
        <h2>Introduction</h2>
        <p>HTML is the foundation of web development...</p>
    </section>
    
    <section>
        <h2>Getting Started</h2>
        <p>To begin with HTML, you need...</p>
    </section>
    
    <section>
        <h2>Advanced Topics</h2>
        <p>Once you master the basics...</p>
    </section>
</article>

<!-- Each section is a themed part of the article --&gt;`} />
                </div>

                <h3>When to Use &lt;section&gt;</h3>
                <ul>
                    <li>Grouping related content with a common theme</li>
                    <li>When content would appear in document outline</li>
                    <li>Chapters of a book or tabs in a UI</li>
                    <li>Different areas of a news homepage</li>
                    <li> NOT for styling purposes (use div)</li>
                    <li> NOT when article or aside is more appropriate</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>The &lt;article&gt; Element</h2>
                <p>
                    Self-contained, independently distributable content that makes sense on its own.
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<main>
    <article>
        <header>
            <h2>Blog Post Title</h2>
            <p>Posted on <time datetime="2024-12-31">Dec 31, 2024</time></p>
            <p>By <a href="/author">John Doe</a></p>
        </header>
        
        <p>Article content goes here...</p>
        
        <footer>
            <p>Tags: <a href="/tag/html">HTML</a>, <a href="/tag/web">Web</a></p>
        </footer>
    </article>
    
    <article>
        <h2>Another Blog Post</h2>
        <p>More content...</p>
    </article>
</main>

<!-- Each article can stand alone independently --&gt;`} />
                </div>

                <h3>When to Use &lt;article&gt;</h3>
                <ul>
                    <li>Blog posts or news articles</li>
                    <li>Forum posts</li>
                    <li>User comments</li>
                    <li>Product cards</li>
                    <li>Social media posts</li>
                    <li>Widget or gadget</li>
                </ul>

                <h3>The "Syndication Test"</h3>
                <p>
                    <strong>Ask:</strong> Would this content make sense if distributed via RSS or shown on another site?
                </p>
                <ul>
                    <li>Yes  Use <code>&lt;article&gt;</code></li>
                    <li> No  Use <code>&lt;section&gt;</code> or <code>&lt;div&gt;</code></li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Section vs Article</h2>

                <div className="comparison-grid">
                    <div className="comparison-card">
                        <h3>&lt;section&gt;</h3>
                        <ul>
                            <li><strong>Purpose:</strong> Thematic grouping</li>
                            <li><strong>Context:</strong> Part of something larger</li>
                            <li><strong>Independent:</strong> No</li>
                            <li><strong>Example:</strong> Chapter in a book</li>
                        </ul>
                    </div>

                    <div className="comparison-card">
                        <h3>&lt;article&gt;</h3>
                        <ul>
                            <li><strong>Purpose:</strong> Complete composition</li>
                            <li><strong>Context:</strong> Standalone content</li>
                            <li><strong>Independent:</strong> Yes</li>
                            <li><strong>Example:</strong> Entire blog post</li>
                        </ul>
                    </div>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Section INSIDE Article</span>
                    </div>
                    <CodeBlock language="html" code={`<article>
    <h1>Complete HTML Guide</h1>
    
    <section>
        <h2>Chapter 1: Basics</h2>
        <p>Content...</p>
    </section>
    
    <section>
        <h2>Chapter 2: Advanced</h2>
        <p>Content...</p>
    </section>
</article>

<!-- Article contains thematic sections --&gt;`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Article INSIDE Section</span>
                    </div>
                    <CodeBlock language="html" code={`<section>
    <h2>Latest News</h2>
    
    <article>
        <h3>News Story 1</h3>
        <p>Content...</p>
    </article>
    
    <article>
        <h3>News Story 2</h3>
        <p>Content...</p>
    </article>
</section>

<!-- Section groups related articles --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The &lt;aside&gt; Element</h2>
                <p>
                    Content tangentially related to main content, like a sidebar or callout.
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<main>
    <article>
        <h1>Main Article Content</h1>
        <p>Article text...</p>
        
        <!-- Aside within article --&gt;
        <aside>
            <h3>Related Info</h3>
            <p>Additional context...</p>
        </aside>
    </article>
    
    <!-- Page-level aside (sidebar) --&gt;
    <aside>
        <h2>About the Author</h2>
        <p>Author bio...</p>
        
        <h2>Related Articles</h2>
        <ul>
            <li><a href="/article1">Article 1</a></li>
            <li><a href="/article2">Article 2</a></li>
        </ul>
    </aside>
</main>`} />
                </div>

                <h3>Common Uses for &lt;aside&gt;</h3>
                <ul>
                    <li>Sidebars</li>
                    <li>Pull quotes</li>
                    <li>Glossary definitions</li>
                    <li>Related links</li>
                    <li>Advertisements (when related to content)</li>
                    <li>Author bios</li>
                    <li>"Fun facts" boxes</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Complete Page Structure Example</h2>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>News Website</title>
</head>
<body>
    <header>
        <h1>Tech News Daily</h1>
        <nav>
            <a href="/">Home</a>
            <a href="/tech">Tech</a>
            <a href="/business">Business</a>
        </nav>
    </header>
    
    <main>
        <!-- Featured articles section --&gt;
        <section>
            <h2>Featured Stories</h2>
            
            <article>
                <header>
                    <h3>AI Breakthrough Announced</h3>
                    <p>By Jane Smith on <time datetime="2024-12-31">Dec 31, 2024</time></p>
                </header>
                
                <p>Scientists have announced...</p>
                
                <aside>
                    <h4>Key Terms</h4>
                    <dl>
                        <dt>Machine Learning</dt>
                        <dd>A subset of AI...</dd>
                    </dl>
                </aside>
                
                <footer>
                    <p>Filed under: <a href="/category/ai">AI</a></p>
                </footer>
            </article>
            
            <article>
                <h3>New Smartphone Released</h3>
                <p>Tech company XYZ...</p>
            </article>
        </section>
        
        <!-- Latest updates section --&gt;
        <section>
            <h2>Latest Updates</h2>
            
            <article>
                <h3>Quick Update 1</h3>
                <p>Short news item...</p>
            </article>
            
            <article>
                <h3>Quick Update 2</h3>
                <p>Another short item...</p>
            </article>
        </section>
        
        <!-- Sidebar --&gt;
        <aside>
            <section>
                <h2>Trending Topics</h2>
                <ul>
                    <li><a href="/topic/ai">Artificial Intelligence</a></li>
                    <li><a href="/topic/crypto">Cryptocurrency</a></li>
                </ul>
            </section>
            
            <section>
                <h2>Newsletter</h2>
                <form>
                    <input type="email" placeholder="Your email">
                    <button>Subscribe</button>
                </form>
            </section>
        </aside>
    </main>
    
    <footer>
        <p>&copy; 2024 Tech News Daily</p>
        <nav>
            <a href="/about">About</a>
            <a href="/contact">Contact</a>
            <a href="/privacy">Privacy</a>
        </nav>
    </footer>
</body>
</html>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Nested Articles</h2>
                <p>
                    Articles can be nested when representing comments, replies, or related content.
                </p>

                <div className="code-example">
                    <CodeBlock language="html" code={`<article>
    <h2>Blog Post Title</h2>
    <p>Main blog post content...</p>
    
    <section>
        <h3>Comments</h3>
        
        <!-- Comment as nested article --&gt;
        <article>
            <header>
                <h4>John Doe says:</h4>
                <time datetime="2024-12-31">Dec 31, 2024</time>
            </header>
            <p>Great article! I learned...</p>
            
            <!-- Reply as nested article --&gt;
            <article>
                <header>
                    <h5>Author replies:</h5>
                </header>
                <p>Thank you for the feedback...</p>
            </article>
        </article>
        
        <article>
            <header>
                <h4>Jane Smith says:</h4>
            </header>
            <p>Another comment...</p>
        </article>
    </section>
</article>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Accessibility Considerations</h2>

                <h3>Always Include Headings</h3>
                <div className="code-example">
                    <div className="code-header">
                        <span>Good: Section with Heading</span>
                    </div>
                    <CodeBlock language="html" code={`<section>
    <h2>Related Products</h2>
    <ul>
        <li>Product 1</li>
        <li>Product 2</li>
    </ul>
</section>`} />
                </div>

                <div className="code-example error">
                    <div className="code-header">
                        <span> Bad: Section without Heading</span>
                    </div>
                    <CodeBlock language="html" code={`<section>
    <!-- No heading! Screen readers struggle --&gt;
    <ul>
        <li>Product 1</li>
        <li>Product 2</li>
    </ul>
</section>`} />
                </div>

                <h3>ARIA Labels for Regions</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Label sections without visible headings --&gt;
<section aria-label="User profile">
    <!-- Content --&gt;
</section>

<!-- Or use aria-labelledby --&gt;
<section aria-labelledby="products-heading">
    <h2 id="products-heading">Our Products</h2>
    <!-- Content --&gt;
</section>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Common Patterns</h2>

                <h3>Blog Layout</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<main>
    <!-- Blog posts --&gt;
    <section>
        <h2>Recent Posts</h2>
        <article>Blog post 1</article>
        <article>Blog post 2</article>
        <article>Blog post 3</article>
    </section>
    
    <!-- Sidebar --&gt;
    <aside>
        <section>Categories</section>
        <section>Archives</section>
    </aside>
</main>`} />
                </div>

                <h3>Product Catalog</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<main>
    <section>
        <h2>Featured Products</h2>
        <article>Product 1</article>
        <article>Product 2</article>
    </section>
    
    <section>
        <h2>On Sale</h2>
        <article>Product 3</article>
        <article>Product 4</article>
    </section>
</main>`} />
                </div>

                <h3>Dashboard</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<main>
    <section>
        <h2>Statistics</h2>
        <!-- Stats widgets --&gt;
    </section>
    
    <section>
        <h2>Recent Activity</h2>
        <article>Activity 1</article>
        <article>Activity 2</article>
    </section>
    
    <aside>
        <h2>Quick Actions</h2>
        <!-- Action buttons --&gt;
    </aside>
</main>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use section for thematic groupings</li>
                            <li>Use article for standalone content</li>
                            <li>Always include headings in sections</li>
                            <li>Nest appropriately (articles in sections, etc.)</li>
                            <li>Use aside for tangential content</li>
                            <li>Test with screen readers</li>
                            <li>Maintain logical heading hierarchy</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Use section/article for styling only</li>
                            <li>Create sections without headings</li>
                            <li>Use article for non-independent content</li>
                            <li>Overuse - sometimes div is fine</li>
                            <li>Forget accessibility</li>
                            <li>Confuse section and article</li>
                            <li>Nest excessively without reason</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLSections;




